import { Box, Typography, List, ListItem } from "@mui/material";
import { Link } from "react-router-dom";
import theme from "../styles/theme";

interface Player {
  id: number;
  username: string;
  nom_complet?: string;
}

interface Category {
  id: number | string;
  name: string;
  icon?: string;
}

interface CategoryRankingProps {
  category: Category;
  ranking: { player: Player; rate: number; count: number }[];
  isSpecial?: boolean;
  totalExercises?: number;
}

export default function CategoryRanking({
  category,
  ranking,
  isSpecial = false,
  totalExercises,
}: CategoryRankingProps) {
  const firstRate = ranking.length > 0 ? ranking[0].rate : 0;

  return (
    <Box
      sx={{
        borderRadius: theme.shape.borderRadius,
        background: theme.palette.background.paperColored,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        p: 2,
        ...(isSpecial && {
          border: `1px solid ${theme.palette.secondary.main}`,
        }),
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontSize: "1.2rem",
          fontWeight: "bold",
          color: isSpecial ? theme.palette.secondary.main : theme.palette.primary.main,
          mb: 2,
        }}
      >
        {category.icon && `${category.icon} `}
        {category.name}
        {totalExercises !== undefined && (
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 1, color: theme.palette.text.secondary }}
          >
            ({totalExercises} exos)
          </Typography>
        )}
      </Typography>

      <List dense>
        {ranking.map((entry, idx) => {
          let color = theme.palette.success.main;
          let colorDark = theme.palette.success.dark;
          if (entry.rate < 30) {
            color = theme.palette.error.dark;
            colorDark = "#c62828";
          } else if (entry.rate < 45) {
            color = theme.palette.error.main;
            colorDark = "#d32f2f";
          } else if (entry.rate < 60) {
            color = theme.palette.warning.main;
            colorDark = "#f57c00";
          } else if (entry.rate < 70) {
            color = theme.palette.warning.light;
            colorDark = "#ffa726";
          } else if (entry.rate < 85) {
            color = theme.palette.info.main;
            colorDark = "#1976d2";
          } else {
            color = theme.palette.success.main;
            colorDark = "#388e3c";
          }

          const isGrayed = !isSpecial && totalExercises !== undefined && entry.count < totalExercises;
          const diff = idx === 0 ? 0 : Math.round(firstRate - entry.rate);

          return (
            <ListItem
              key={entry.player.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                opacity: isGrayed ? 0.75 : 1,
                filter: isGrayed ? "grayscale(0.9)" : "none",
              }}
            >
              <Box
                component={Link}
                to={`/player/${entry.player.id}`}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography component="span" sx={{ fontWeight: "bold" }}>
                  {idx + 1}. {entry.player.nom_complet || entry.player.username}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  {entry.count} exos
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {idx !== 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.9rem",
                      ml: 1,
                    }}
                  >
                    -{diff}
                  </Typography>
                )}
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: theme.shape.borderRadius,
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "white",
                    background: `linear-gradient(135deg, ${color} 0%, ${colorDark} 100%)`,
                    minWidth: "58px",
                    textAlign: "center",
                    boxShadow: `0 2px 8px ${color}40`,
                    border: `1px solid ${colorDark}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    letterSpacing: "0.5px",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: `0 4px 12px ${color}60`,
                    },
                  }}
                >
                  {entry.rate}%
                </Box>
                
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
